import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { Logo } from "@/components/Logo";
import { FOOTER_FEATURES } from "@/lib/footerFeatures";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Phone, Mail, ShieldCheck, Store, Smartphone, Truck, Users, Award, CheckCircle2, SlidersHorizontal, Sparkles } from "lucide-react";

const FOOTER_STRIP = [
  { key: "confiance", icon: ShieldCheck, color: "text-emerald-600" },
  { key: "pour_tous", icon: Store, color: "text-mango-600" },
  { key: "simple", icon: Smartphone, color: "text-teal-500" },
  { key: "rapide", icon: Truck, color: "text-red-500" },
  { key: "communaute", icon: Users, color: "text-emerald-600" },
  { key: "qualite", icon: Award, color: "text-mango-600" },
];

const KENTE = {
  backgroundImage:
    "repeating-linear-gradient(45deg, #065F46 0 14px, #D97706 14px 28px, #166534 28px 42px, #EA580C 42px 56px)",
};

export const Footer = () => {
  const { t, lang } = useLang();
  const [active, setActive] = useState(null);
  const info = active ? FOOTER_FEATURES[active]?.[lang] : null;
  const ActiveIcon = FOOTER_STRIP.find((s) => s.key === active)?.icon || ShieldCheck;

  return (
    <footer className="bg-stone-900 text-stone-300 mt-24 grain">
      <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="mb-3"><Logo light /></div>
          <p className="font-script text-mango-500 text-2xl leading-none mb-3">{t("brand_tagline")}</p>
          <p className="text-sm text-stone-400 leading-relaxed">{t("footer_tag")}</p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">{t("quick_links")}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-mango-500 transition-colors">{t("nav_home")}</Link></li>
            <li><Link to="/catalog" className="hover:text-mango-500 transition-colors">{t("nav_catalog")}</Link></li>
            <li><Link to="/orders" className="hover:text-mango-500 transition-colors">{t("nav_orders")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">{t("categories")}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/catalog?category=food" className="hover:text-mango-500 transition-colors">Alimentation</Link></li>
            <li><Link to="/catalog?category=fashion" className="hover:text-mango-500 transition-colors">Mode & Pagnes</Link></li>
            <li><Link to="/catalog?category=crafts" className="hover:text-mango-500 transition-colors">Artisanat</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">{t("contact")}</h4>
          <ul className="space-y-3 text-sm text-stone-400">
            <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-mango-500" /> Marché d'Assiganme, Boutiques et distributeurs, Lomé, Togo</li>
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-mango-500" /> +228 93 00 66 03</li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-mango-500" /> contact@agonami.com</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-stone-800 py-6 text-center text-xs text-stone-500">
        © 2026 AGOO MARKET — Publier vos produits - Lomé, Togo. AGONAMI Tous droits réservés.
      </div>

      {/* Clickable feature strip after footer */}
      <div className="bg-sand-50" data-testid="footer-features">
        <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {FOOTER_STRIP.map(({ key, icon: Icon, color }) => {
            const it = FOOTER_FEATURES[key][lang];
            return (
              <button key={key} onClick={() => setActive(key)} data-testid={`footer-feature-${key}`}
                className="group flex items-center gap-3 text-left rounded-xl p-2.5 transition-all duration-200 hover:bg-white hover:shadow-md">
                <span className="w-10 h-10 rounded-full bg-white grid place-items-center shrink-0 shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-110">
                  <Icon className={`w-5 h-5 ${color}`} />
                </span>
                <div className="min-w-0">
                  <p className="font-bold text-xs uppercase tracking-wider text-stone-800 leading-tight">{it.title}</p>
                  <p className="text-[11px] text-stone-500 leading-tight mt-0.5 truncate">{it.subtitle}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div className="h-2.5 w-full" style={KENTE} />

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden rounded-2xl border-0 gap-0" data-testid="footer-feature-modal">
          {info && (
            <>
              <div className="relative bg-emerald-800 text-white p-6 grain overflow-hidden">
                <span className="absolute top-0 right-0 w-40 h-40 bg-mango-500/20 rounded-full blur-3xl -mr-12 -mt-12" />
                <span className="relative w-14 h-14 rounded-2xl bg-white/10 grid place-items-center mb-3"><ActiveIcon className="w-7 h-7 text-mango-400" /></span>
                <DialogTitle className="font-display font-extrabold text-2xl tracking-tight">{info.title}</DialogTitle>
                <p className="text-emerald-100 text-sm mt-1">{info.subtitle}</p>
              </div>
              <div className="p-6 bg-sand-50 space-y-6 max-h-[60vh] overflow-y-auto">
                <p className="text-stone-600 leading-relaxed" data-testid="footer-feature-desc">{info.desc}</p>
                <div>
                  <h3 className="font-display font-bold text-emerald-800 mb-3 flex items-center gap-2 uppercase text-sm tracking-wider"><SlidersHorizontal className="w-4 h-4 text-mango-600" /> {t("how_it_works")}</h3>
                  <ol className="space-y-2.5">
                    {info.how.map((s, i) => (
                      <li key={i} className="flex gap-3 items-start">
                        <span className="w-6 h-6 rounded-full bg-mango-600 text-white text-xs font-bold grid place-items-center shrink-0">{i + 1}</span>
                        <span className="text-sm text-stone-700 leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                <div>
                  <h3 className="font-display font-bold text-emerald-800 mb-3 flex items-center gap-2 uppercase text-sm tracking-wider"><Sparkles className="w-4 h-4 text-mango-600" /> {t("features_provided")}</h3>
                  <ul className="space-y-2">
                    {info.functions.map((f, i) => (
                      <li key={i} className="flex gap-2 items-start">
                        <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                        <span className="text-sm text-stone-700 leading-relaxed">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </footer>
  );
};
